/**
 * Text Processor – Erkennt das Eingabeformat und bereinigt den Text.
 * Unterstützt: Markdown, HTML, Word (.docx), Plain Text
 */
import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Tags und Elemente, die als "Rauschen" entfernt werden sollen
 */
const NOISE_TAGS = [
  'nav',
  'header',
  'footer',
  'aside',
  'menu',
  'menuitem',
  'script',
  'style',
  'noscript',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'select',
  'textarea',
  'svg',
  'canvas',
  'video',
  'audio',
];

const NOISE_SELECTORS = [
  '[role="navigation"]',
  '[role="banner"]',
  '[role="contentinfo"]',
  '[role="complementary"]',
  '.nav',
  '.navbar',
  '.navigation',
  '.menu',
  '.sidebar',
  '.footer',
  '.header',
  '.breadcrumb',
  '.pagination',
  '.ad',
  '.ads',
  '.advertisement',
  '.cookie-banner',
  '.social-share',
  '.share-buttons',
  '.comments',
  '#nav',
  '#menu',
  '#sidebar',
  '#footer',
  '#header',
];

/**
 * Erkennt das Format des Eingabetexts
 * @param {string} text
 * @returns {'markdown' | 'html' | 'text'}
 */
export function detectFormat(text) {
  if (!text || typeof text !== 'string') return 'text';

  const trimmed = text.trim();

  // HTML detection: starts with a tag or contains significant HTML
  if (/<(!DOCTYPE|html|head|body|div|p|span|h[1-6]|ul|ol|table|article|section)\b/i.test(trimmed)) {
    return 'html';
  }

  // Markdown detection: headers, lists, links, bold, etc.
  const mdPatterns = [
    /^#{1,6}\s+/m, // Headers
    /^\s*[-*+]\s+/m, // Unordered lists
    /^\s*\d+\.\s+/m, // Ordered lists
    /\[.+?\]\(.+?\)/, // Links
    /\*\*.+?\*\*/, // Bold
    /\*.+?\*/, // Italic
    /^>\s+/m, // Blockquotes
    /```[\s\S]*?```/, // Code blocks
    /^\s*---\s*$/m, // Horizontal rules
  ];

  const mdMatches = mdPatterns.filter((p) => p.test(trimmed)).length;
  if (mdMatches >= 1) return 'markdown';

  return 'text';
}

/**
 * Entfernt HTML-Rauschen (Navigation, Menüs, Werbung, etc.)
 * @param {string} html
 * @returns {string} Bereinigtes HTML
 */
export function removeNoise(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Remove noise tags
  for (const tag of NOISE_TAGS) {
    doc.querySelectorAll(tag).forEach((el) => el.remove());
  }

  // Remove noise selectors
  for (const selector of NOISE_SELECTORS) {
    try {
      doc.querySelectorAll(selector).forEach((el) => el.remove());
    } catch {
      // Invalid selector, skip
    }
  }

  // Remove hidden elements
  doc
    .querySelectorAll(
      '[hidden], [aria-hidden="true"], [style*="display: none"], [style*="display:none"]'
    )
    .forEach((el) => el.remove());

  // Remove empty elements (but keep <br>, <hr>)
  doc.querySelectorAll('div, span, p').forEach((el) => {
    if (!el.textContent.trim() && !el.querySelector('img')) {
      el.remove();
    }
  });

  // Try to find main content area
  const mainContent = doc.querySelector(
    'main, article, [role="main"], .content, .article, #content, #main'
  );
  if (mainContent) {
    return mainContent.innerHTML;
  }

  return doc.body ? doc.body.innerHTML : html;
}

/** Erlaubte Auszeichnung für angezeigten Text */
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'br',
    'hr',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'ul',
    'ol',
    'li',
    'blockquote',
    'pre',
    'code',
    'a',
    'img',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'div',
    'span',
  ],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class'],
};

/**
 * Entfernt alles außer harmloser Textauszeichnung. Gilt für eigene Eingaben wie
 * für Texte, die über die Synchronisation von anderen Geräten kommen.
 * @param {string} html
 * @returns {string}
 */
export function sanitizeHtml(html) {
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

/**
 * Konvertiert Markdown zu sauberem HTML
 * @param {string} markdown
 * @returns {string}
 */
export function markdownToHtml(markdown) {
  const rawHtml = marked.parse(markdown, {
    breaks: true,
    gfm: true,
  });
  return sanitizeHtml(rawHtml);
}

/**
 * Konvertiert HTML zu sauberem, darstellbarem HTML
 * @param {string} html
 * @returns {string}
 */
export function cleanHtml(html) {
  return sanitizeHtml(removeNoise(html));
}

/**
 * Hilfsfunktion zur Erkennung, ob eine Zeile wahrscheinlich eine Überschrift ist.
 * Kriterien: Kurz (<80 Zeichen), wenige Wörter (<8), keine Satzzeichen am Ende, startet mit Alphanumerischem Zeichen.
 * @param {string} str
 * @returns {boolean}
 */
function isLikelyHeading(str) {
  const trimmed = str.trim();
  if (!trimmed) return false;

  // Maximal 80 Zeichen
  if (trimmed.length > 80) return false;

  // Maximal 8 Wörter
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length > 8) return false;

  // Darf nicht mit typischen Satzenden enden (. ! ? : , ;)
  const lastChar = trimmed[trimmed.length - 1];
  if (/[.!?,,;:]/.test(lastChar)) return false;

  // Muss mit einem Buchstaben, einer Zahl oder Anführungszeichen beginnen
  if (!/^[a-zA-Z0-9üäöÜÄÖß"“'‘]/.test(trimmed)) return false;

  return true;
}

/**
 * Konvertiert Plain Text zu HTML (Absätze und Überschriften erkennen)
 * @param {string} text
 * @returns {string}
 */
export function plainTextToHtml(text) {
  if (!text) return '';
  const lines = text.split('\n');
  const blocks = [];
  let current = [];

  for (const line of lines) {
    if (line.trim() === '') {
      if (current.length > 0) {
        blocks.push(current.join('<br>'));
        current = [];
      }
    } else {
      current.push(DOMPurify.sanitize(line));
    }
  }

  if (current.length > 0) {
    blocks.push(current.join('<br>'));
  }

  return blocks
    .map((block) => {
      // Wenn der Block eine einzige Zeile ohne Zeilenumbruch ist und wie eine Überschrift aussieht
      if (!block.includes('<br>') && isLikelyHeading(block)) {
        return `<h2>${block}</h2>`;
      }
      return `<p>${block}</p>`;
    })
    .join('');
}

/**
 * Verarbeitet einen Word-Buffer (.docx) zu HTML
 * @param {ArrayBuffer} buffer
 * @returns {Promise<string>}
 */
export async function processDocx(buffer) {
  const mammoth = await import('mammoth');
  const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
  return DOMPurify.sanitize(result.value);
}

/**
 * Extrahiert reinen Text aus HTML (für TTS)
 * @param {string} html
 * @returns {string}
 */
export function extractPlainText(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body ? doc.body.textContent.trim() : '';
}

/**
 * Hauptfunktion: Verarbeitet Eingabe je nach Format
 * @param {string} input
 * @param {'markdown' | 'html' | 'text' | 'auto'} format
 * @returns {string} Bereinigtes HTML zur Anzeige
 */
export function processText(input, format = 'auto') {
  if (!input || typeof input !== 'string') return '';

  const detectedFormat = format === 'auto' ? detectFormat(input) : format;

  switch (detectedFormat) {
    case 'markdown':
      return markdownToHtml(input);
    case 'html':
      return cleanHtml(input);
    case 'text':
    default:
      return plainTextToHtml(input);
  }
}
